import React, {
  useEffect,
  useState,
} from "react";
import {Button, Spin} from "antd";
import {action, getMrn, getPayPalClientToken} from "../../api";
import { Base64 } from "js-base64";
import currencyformatter from "../../utils/currencyformatter";

type Props = {
    chargeByApple: Function;
    subscriptionData: Object;
    link: String;
    accountGid: String;
    successCallback: Function;
    failCallback: Function;
    recaptchaToken: String;
};

const paypal_sdk_url = "https://www.paypal.com/sdk/js";
const currency = "USD";
const intent = "capture";

async function script_to_head(attrs: Record<string, string>) {
    return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        Object.entries(attrs).forEach(([k, v]) => script.setAttribute(k, v));
        script.onload = () => resolve();
        script.onerror = () => reject();
        document.head.appendChild(script);
    });
}

// --- Helpers ---
const existsApplePayJsApi = async () => {
    try {
        return !!(
            window.ApplePaySession &&
            (await window.ApplePaySession.canMakePayments())
        );
    } catch {
        return false;
    }
};

// --- Event Handlers ---
const getOnCancel = (resolve: any, reject: any) => () => {
    reject("Payment cancelled");
};

const getOnValidateMerchant = (session: any) => async (event: any) => {
    try {
        const applepay = window.paypal.Applepay();
        const payload = await applepay.validateMerchant({
            validationUrl: event.validationURL,
            displayName: "My Shop",
        });
        session.completeMerchantValidation(payload.merchantSession);
    } catch (err) {
        console.error("Merchant validation failed:", err);
        session.abort();
    }
};

// --- Main Component ---
const ApplePayButton = (props: Props) => {
  const {chargeByApple, subscriptionData, link, accountGid, successCallback, failCallback} = props;
  const [eligible, setEligible] = useState(false);
  const [loading, setLoading] = useState(false);

  const checkAppleEligible = async () => {
    setLoading(true);
    const enabled = await existsApplePayJsApi();
    if (!enabled || !accountGid) return;
    try {
        const data = await getPayPalClientToken(accountGid);
        const value = Base64.decode(data);
        const value_json = JSON.parse(value);
        await script_to_head({
          src: `${paypal_sdk_url}?client-id=${value_json?.clientId}&enable-funding=venmo&currency=${currency}&intent=${intent}&merchant-id=${value_json?.merchantId}&components=buttons,applepay`,
          "data-client-token": value_json?.token?.client_token,
        });

        if (!window.paypal?.Applepay) return;
        const config = await window.paypal.Applepay().config();
        setEligible(config.isEligible);
      } catch (err) {
        console.error("Apple Pay setup failed:", err);
        setEligible(false);
      } finally {
        setLoading(false);
      }
  };

  useEffect(() => {
    checkAppleEligible();
  }, []);

    const getPaymentRequest = (currencyCode, items, label, amount) => {
        const paymentRequest = {
            currencyCode: currencyCode,
            countryCode: 'US',
            lineItems: items,
            total: {
                label: label,
                amount: amount
            },
            supportedNetworks: ['masterCard', 'visa'],
            merchantCapabilities: ['supports3DS', 'supportsCredit', 'supportsDebit'],
            requiredShippingContactFields: [
                "postalAddress",
                "name",
                "phone",
                "email"
            ],
            requiredBillingContactFields: [
                "postalAddress",
                "email",
                "name",
                "phone"
            ]
        };

        if(subscriptionData) {
            paymentRequest.recurringPaymentRequest = {
                paymentDescription: subscriptionData?.description,
                regularBilling: {
                    label: label,
                    amount: amount,
                    type: 'final',
                    paymentTiming: 'recurring',
                    recurringPaymentStartDate: subscriptionData.plan2StartDate
                        ? new Date(subscriptionData.plan2StartDate)
                        : null,

                    recurringPaymentEndDate: subscriptionData.plan2EndDate
                        ? new Date(subscriptionData.plan2EndDate)
                        : null,

                    recurringPaymentIntervalUnit: subscriptionData.plan2BillingFrequency?.toLowerCase(), // "month"
                    recurringPaymentIntervalCount: subscriptionData.plan2BillingPeriod ?? 1,
                },
                managementURL: link,
            };
        }
        console.log('apple pay request: ', JSON.stringify(paymentRequest, null, 2));
        return paymentRequest;
    }

    const getOnPaymentAuthorized = (session: any, resolve: any, reject: any) => {
        return async (event: any) => {
            try {
              const payment = event.payment;
              const customerName = ((payment.billingContact.givenName) ? payment.billingContact.givenName : "") + (payment.billingContact.familyName ? payment.billingContact.familyName : "")
              const customerEmail = payment.shippingContact.emailAddress ? payment.shippingContact.emailAddress : ""
              const countryCode = payment.billingContact.countryCode ? payment.billingContact.countryCode : ""
              const stateCode = payment.billingContact.administrativeArea ? payment.billingContact.administrativeArea : ""
              const city = payment.billingContact.locality ? payment.billingContact.locality : ""
              const street = payment.billingContact.addressLines[0] ? payment.billingContact.addressLines[0] : ""
              const postalCode = payment.billingContact.postalCode ? payment.billingContact.postalCode : ""
              const cardNetwork = payment.token.paymentMethod.network ? payment.token.paymentMethod.network : ""
              const cardType = payment.token.paymentMethod.network ? payment.token.paymentMethod.network : ""
              const cardLast4 = payment.token.paymentMethod.displayName ? payment.token.paymentMethod.displayName : ""
              const deviceData = `${payment.token.paymentData.data}&ectype=apple&ecsig=${payment.token.paymentData.signature}&eckey=${payment.token.paymentData.header.ephemeralPublicKey}&ectid=${payment.token.transactionIdentifier}&echash=&ecpublickeyhash=${payment.token.paymentData.header.publicKeyHash}`;

                const customerPhone = payment.shippingContact?.phoneNumber
                    ? `+1${payment.shippingContact.phoneNumber}`
                    : "";
                // Step 1: create PayPal order via backend
                const resEntity = await chargeByApple({
                    deviceData,
                    amount: payment.amount,
                    customerName,
                    customerEmail,
                    customerPhone,
                    countryCode,
                    stateCode,
                    street,
                    postalCode,
                    city,
                    cardNetwork,
                    cardType,
                    cardLast4,
                  },
                  props?.recaptchaToken,
                );
                const orderId = await getMrn(resEntity.paymentSessionGid, resEntity.psClientSecret);
                // Step 2: confirm PayPal order
                await window.paypal.Applepay().confirmOrder({
                    orderId,
                    token: payment.token,
                    billingContact: payment.billingContact,
                });
                // Step 3: capture/perform final action
                await action(resEntity.paymentSessionGid, resEntity.psClientSecret);
                session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
                successCallback(resEntity);
                resolve({ orderId });
            } catch (err) {
                console.error("Apple Pay flow error:", err);
                session.completePayment(window.ApplePaySession.STATUS_FAILURE);
                await failCallback();
                reject(err);
            }
        };
    };

  const performApplePayPayment = (currencyCode: string, items: any[], label: string, amount: string) => {
    return new Promise((resolve, reject) => {
      const session = new window.ApplePaySession(
        4,
        getPaymentRequest(currencyCode, items, label, amount)
      );

      session.oncancel = getOnCancel(resolve, reject);
      session.onvalidatemerchant = getOnValidateMerchant(session);
      session.onpaymentauthorized = getOnPaymentAuthorized(session, resolve, reject);

      session.begin();
    });
  };

  const handleClick = () => {
    if (Number.isNaN(props?.amount)) return;
    const payableAmount = (props.amount / 100).toFixed(2);
    performApplePayPayment(
      currency,
      [
        {
          label: props?.payment?.description ?? '',
          amount: payableAmount,
        },
      ],
      props?.payment?.description ?? '',
      payableAmount
    ).then((result) => {
      console.log("✅ Payment success:", result);
    }).catch((err) => {
      console.error("❌ Payment failed:", err);
    });
  };

  if (loading) return (
    <Spin
      spinning={loading.current}
      tip="Please wait..."
      size="large"
      delay={300}
    />
  );

  if (!eligible) return <p>Not Eligible</p>;

  return (
    <Button
      onClick={handleClick}
      className="bg-black rounded-lg px-6 py-3 hover:bg-gray-800"
    >
      <span role="img" aria-label="apple"></span> Pay
    </Button>
  );
};

export default ApplePayButton;
